import { useEffect, useState } from 'react';

const HINT_KEY = 'pm_swipe_hint_seen_v1';

function hasSeenHint(): boolean {
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false; // storage disabled -- show it every session rather than never
  }
}

function markHintSeen(): void {
  try {
    window.localStorage.setItem(HINT_KEY, '1');
  } catch {
    // best-effort only
  }
}

/** One-time, dismissible discoverability hint for canvas swipe input
 * (GameCanvas already accepts swipes -- this just makes players AWARE of
 * it, since the D-pad is the only visually obvious control). Rendered as a
 * normal flow child of `.stage-top` (see GameScreen.tsx) -- NOT an overlay
 * on top of `.game-canvas` -- so useGameLayout's real measurement of
 * `.stage-top`'s height (already ResizeObserver'd) automatically accounts
 * for its extra space and shrinks the canvas budget accordingly; showing
 * or dismissing it can never clip anything. Shown once ever, persisted via
 * localStorage -- "do not spam it". */
export function SwipeHint(): JSX.Element | null {
  const [dismissed, setDismissed] = useState(hasSeenHint());

  function dismiss(): void {
    markHintSeen();
    setDismissed(true);
  }

  // Auto-dismiss after a few seconds so it never lingers over the maze.
  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(dismiss, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <div className="swipe-hint" role="status" data-testid="swipe-hint">
      <span>Swipe anywhere or use the pad</span>
      <button type="button" className="swipe-hint-dismiss" onClick={dismiss} aria-label="Dismiss hint">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  );
}
